import { useLayoutEffect, useState } from 'react';

interface UseToolContainerOverlayPositionOptions {
  open: boolean;
  anchorId?: string;
  sideOffset?: number;
  viewportPadding?: number;
}

export interface ToolContainerOverlayPosition {
  top: number;
  left: number;
  height: number;
  availableWidth: number;
}

const DEFAULT_OVERLAY_POSITION: ToolContainerOverlayPosition = {
  top: 64,
  left: 404,
  height: 0,
  availableWidth: 640,
};

/**
 * 计算依附在 tool-container 右侧的浮层位置。
 * 通过 ResizeObserver 监听容器尺寸变化，确保侧边栏展开/收起后能自动更新。
 */
export function useToolContainerOverlayPosition({
  open,
  anchorId = 'tool-container',
  sideOffset = 0,
  viewportPadding = 32,
}: UseToolContainerOverlayPositionOptions): ToolContainerOverlayPosition {
  const [position, setPosition] = useState<ToolContainerOverlayPosition>(DEFAULT_OVERLAY_POSITION);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const anchorElement = document.getElementById(anchorId);
    if (!anchorElement) {
      return;
    }

    let frameId = 0;

    const updatePosition = () => {
      frameId = 0;

      const rect = anchorElement.getBoundingClientRect();
      const left = rect.right + sideOffset;
      const availableWidth = Math.max(0, window.innerWidth - left - viewportPadding);

      setPosition({
        top: rect.top,
        left,
        height: rect.height,
        availableWidth,
      });
    };

    const scheduleUpdate = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(updatePosition);
    };

    scheduleUpdate();

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(anchorElement);
    anchorElement.parentElement && resizeObserver.observe(anchorElement.parentElement);

    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, { passive: true });

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate);
    };
  }, [anchorId, open, sideOffset, viewportPadding]);

  return position;
}
